/**
 * Tier Definitions — Internal Access Control
 * 
 * NOTE: These tiers control DEPLOY RIGHTS only, NOT customer-facing pricing.
 * Customer-facing pricing lives in src/lib/stacks/stackCatalog.ts (Stacks-only model).
 * 
 * Three-tier model: Core, Pro, Agency
 * Tiers control deploy rights, not Flow visibility.
 */

import type { CapabilityPackId } from "@/lib/capabilities/types";

export type SubscriptionTier = "core" | "pro" | "agency";

/**
 * Deploy rights determine what a user can install into their workspace
 */
export type DeployRight = 
  | "outputs"      // Deliverables (Core+)
  | "tasks"        // Tasks deploy (Pro+)
  | "assets"       // Assets deploy (Pro+)
  | "automations"  // Automation deploy (Pro+)
  | "crm"          // CRM schema deploy (Agency)
  | "provisioning" // Multi-client provisioning (Agency)
  | "policies";    // Policy admin (Agency)

export interface TierDefinition {
  id: SubscriptionTier;
  name: string;
  nameNl: string;
  priceEur: number;
  description: string;
  descriptionNl: string;
  deployRights: DeployRight[];
  features: string[];
  featuresNl: string[];
  capabilities: CapabilityPackId[];
}

/**
 * Tier definitions with deploy rights and features
 */
export const TIERS: Record<SubscriptionTier, TierDefinition> = {
  core: {
    id: "core",
    name: "Core",
    nameNl: "Core",
    priceEur: 99,
    description: "Revenue Suite",
    descriptionNl: "Revenue Suite",
    deployRights: ["outputs"],
    features: [
      "Revenue Suite included",
      "Lead Magnet System",
      "Outbound Engine",
      "Follow-Up System",
      "Runs (werkbewijzen)",
      "Community support",
    ],
    featuresNl: [
      "Revenue Suite inbegrepen",
      "Lead Magnet Systeem",
      "Outbound Engine",
      "Follow-Up Systeem",
      "Runs (werkbewijzen)",
      "Community support",
    ],
    capabilities: ["EXECUTION_CORE"],
  },
  pro: {
    id: "pro",
    name: "Pro",
    nameNl: "Pro",
    priceEur: 199,
    description: "Revenue + Demand Suites",
    descriptionNl: "Revenue + Demand Suites",
    deployRights: ["outputs", "tasks", "assets", "automations"],
    features: [
      "Everything in Core",
      "Demand Suite included",
      "Authority Content System",
      "Content Engine",
      "Deploy tasks + assets",
      "Deploy automations",
      "Priority support",
    ],
    featuresNl: [
      "Alles van Core",
      "Demand Suite inbegrepen",
      "Authority Content Systeem",
      "Content Engine",
      "Deploy tasks + assets",
      "Deploy automations",
      "Priority support",
    ],
    capabilities: ["EXECUTION_CORE", "SALES", "AUTOMATIONS"],
  },
  agency: {
    id: "agency",
    name: "Agency",
    nameNl: "Agency",
    priceEur: 299,
    description: "All Suites + HQ",
    descriptionNl: "Alle Suites + HQ",
    deployRights: ["outputs", "tasks", "assets", "automations", "crm", "provisioning", "policies"],
    features: [
      "Everything in Pro",
      "Growth Suite",
      "HQ dashboard access",
      "Multi-client management",
      "Reseller Mode",
      "Operator Mode",
      "Policies & governance",
      "CRM schema deploy",
    ],
    featuresNl: [
      "Alles van Pro",
      "Growth Suite",
      "HQ dashboard toegang",
      "Multi-client beheer",
      "Reseller Mode",
      "Operator Mode",
      "Policies & governance",
      "CRM schema deploy",
    ],
    capabilities: [
      "EXECUTION_CORE",
      "SALES",
      "AUTOMATIONS",
      "OPERATOR_CONSOLE",
      "MULTI_ACCOUNT",
      "RESELLER",
      "POLICY_ADMIN",
    ],
  },
};

/**
 * Check if a tier has a specific deploy right
 */
export function canDeploy(tier: SubscriptionTier, right: DeployRight): boolean {
  return TIERS[tier].deployRights.includes(right);
}

/**
 * Get the minimum tier required for a deploy right
 */
export function getMinTierForRight(right: DeployRight): SubscriptionTier {
  if (right === "outputs") return "core";
  if (["tasks", "assets", "automations"].includes(right)) return "pro";
  return "agency";
}

/**
 * Normalize legacy tiers to new 3-tier model
 * Maps: Core → core, Guided/Managed/FullSuite → pro, Agency → agency
 */
export function normalizeTier(tier: string | null): SubscriptionTier {
  if (!tier) return "core";
  const lower = tier.toLowerCase();
  
  if (lower === "core" || lower === "start") return "core";
  if (["guided", "managed", "fullsuite", "pro"].includes(lower)) return "pro";
  if (["agency", "business"].includes(lower)) return "agency";
  
  return "core";
}

/**
 * Get upgrade target tier
 */
export function getUpgradeTier(currentTier: SubscriptionTier): SubscriptionTier | null {
  if (currentTier === "core") return "pro";
  if (currentTier === "pro") return "agency";
  return null;
}

/**
 * Tier level for comparison (higher = more permissions)
 */
export const TIER_LEVELS: Record<SubscriptionTier, number> = {
  core: 1,
  pro: 2,
  agency: 3,
};

/**
 * Check if tierA is at least tierB level
 */
export function isAtLeastTier(tierA: SubscriptionTier, tierB: SubscriptionTier): boolean {
  return TIER_LEVELS[tierA] >= TIER_LEVELS[tierB];
}

/**
 * Get display name for tier
 */
export function getTierDisplayName(tier: SubscriptionTier | null, locale: "en" | "nl" = "nl"): string {
  if (!tier) return locale === "nl" ? "Geen plan" : "No plan";
  return locale === "nl" ? TIERS[tier].nameNl : TIERS[tier].name;
}

/**
 * Get all tiers as array
 */
export function getAllTiers(): TierDefinition[] {
  return Object.values(TIERS);
}
